import { fromScaled, toScaled } from '@colplat/protocol';
import { isTrue, type AccountRow, type Ledger } from './rows.js';

/**
 * The chart of accounts with its balances, computed from rows so that it
 * computes in both places (ADR-0025).
 *
 * This was the last page in webledger still answered only by a query. The
 * balances came from a `GROUP BY` in `services/api`, so the Accounts page
 * could not add them up on its own — which cost two things at once. Offline
 * it had nothing but whatever the response cache happened to hold; and while
 * online it had no way to notice a transaction had changed, because the only
 * thing that could tell it was a request it had no reason to repeat. Somebody
 * else's save in another window left every total on the page quietly wrong.
 *
 * Reading it from the replica instead makes both the same fact: the page
 * recomputes whenever the local rows move, and the rows move whether the
 * change came from this device or from a sync.
 */

export interface AccountBalancesDto {
  id: string;
  parentId: string | null;
  name: string;
  type: string;
  currencyCode: string;
  isPlaceholder: boolean;
  isMulticurrency: boolean;
  sortOrder: number;
  description: string;
  /** Bare filename of the seeded icon, e.g. `000-assets.svg`. Empty when none. */
  iconPath: string;
  /**
   * Per-(account, currency) balance of this account's OWN legs, all time, no
   * date bound — legacy's `tree` query 2. A leg's own currency wins over the
   * account's.
   *
   * Deliberately **not** rolled up to parents: the client aggregates subtree
   * totals, because it is also the thing that knows which subtrees are
   * collapsed and which view is showing. Rolling up here would mean producing
   * one number the page then has to take apart again.
   */
  balances: Record<string, string>;
  purposeIds: string[];
}

/**
 * A currency an account has legs in is present even when they cancel out.
 *
 * `fixed()` in wallets.ts drops a zero, and is right to: a wallet holding
 * nothing in a currency is not holding that currency. Here the opposite is
 * true — an account that took in 500 and paid out 500 has a euro balance, and
 * it is zero. Dropping it would make the page say "–", which reads as "this
 * account has never seen a euro" and is a different claim entirely.
 */
function balancesOf(sums: ReadonlyMap<string, bigint>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [ccy, total] of sums) out[ccy] = fromScaled(total);
  return out;
}

/** Shared because it is never written to — one allocation, not one per account. */
const EMPTY: ReadonlyMap<string, bigint> = new Map<string, bigint>();

export function accountsView(ledger: Ledger): AccountBalancesDto[] {
  const live = new Set(ledger.transactions().map((t) => t.id));
  const sums = new Map<string, Map<string, bigint>>();
  for (const leg of ledger.splits()) {
    // A leg belonging to a deleted transaction is not a balance. The server's
    // query says the same thing with a join to `transactions_v`; here the
    // transaction simply is not in the ledger, so the legs it carried are
    // gone with it — except on a device mid-sync, which is why this is
    // checked rather than assumed.
    if (!live.has(leg.transactionId)) continue;
    let forAccount = sums.get(leg.accountId);
    if (!forAccount) { forAccount = new Map(); sums.set(leg.accountId, forAccount); }
    forAccount.set(leg.currencyCode, (forAccount.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount));
  }

  const purposeIds = new Map<string, string[]>();
  for (const link of ledger.accountPurposes()) {
    purposeIds.set(link.accountId, [...(purposeIds.get(link.accountId) ?? []), link.purposeId]);
  }

  return [...ledger.accounts()]
    .sort((a: AccountRow, b: AccountRow) => a.name.localeCompare(b.name))
    .map((a) => ({
      id: a.id,
      parentId: a.parentId,
      name: a.name,
      type: a.type,
      currencyCode: a.currencyCode,
      isPlaceholder: isTrue(a.isPlaceholder),
      isMulticurrency: isTrue(a.isMulticurrency),
      sortOrder: a.sortOrder ?? 0,
      description: a.description ?? '',
      iconPath: a.iconPath ?? '',
      // `{}` and `[]` rather than absent, so every consumer can index without
      // a guard — legacy casts to an object for the same reason.
      balances: balancesOf(sums.get(a.id) ?? EMPTY),
      purposeIds: purposeIds.get(a.id) ?? [],
    }));
}

/**
 * An account's balance in one currency just before an entry, in the order
 * the registers walk: by date, then `seq`, then transaction id.
 *
 * What the short form's account assistant shows as "before" — so "after" is
 * simply this plus the entry's own leg. The entry's own legs are left out
 * wherever they sit, because the question is what this entry finds when it
 * arrives. A new entry has no id yet and is taken as the last of its day.
 *
 * The account alone, not its sub-accounts: a leg is booked to one account,
 * and the figure beside it is that account's.
 */
export function accountBalanceBefore(
  ledger: Ledger, accountId: string, currencyCode: string,
  at: { txDate: string; transactionId?: string | undefined },
): string {
  const earlier = enteredBefore(ledger, at);
  let sum = 0n;
  for (const leg of ledger.splits()) {
    if (leg.accountId !== accountId || leg.currencyCode !== currencyCode) continue;
    if (earlier(leg.transactionId)) sum += toScaled(leg.amount);
  }
  return fromScaled(sum);
}

/**
 * Whether a transaction comes before an entry, in the registers' order — by
 * date, then `seq`, then id — leaving the entry itself out. A new entry has
 * no id yet and is taken as the last of its day; a transaction that no
 * longer exists comes before nothing.
 *
 * The one rule behind every "before this entry" figure the short form shows,
 * so the account line and the wallet heads-up cannot disagree about which
 * entries already happened.
 */
export function enteredBefore(
  ledger: Ledger, at: { txDate: string; transactionId?: string | undefined },
): (transactionId: string) => boolean {
  const txById = new Map(ledger.transactions().map((t) => [t.id, t] as const));
  const self = at.transactionId === undefined ? undefined : txById.get(at.transactionId);
  return (transactionId) => {
    if (transactionId === at.transactionId) return false;
    const tx = txById.get(transactionId);
    if (!tx) return false;
    return tx.txDate < at.txDate
      || (tx.txDate === at.txDate && (self === undefined || self.txDate !== at.txDate
        || (tx.seq ?? 0) < (self.seq ?? 0)
        || ((tx.seq ?? 0) === (self.seq ?? 0) && tx.id < self.id)));
  };
}
